import type React from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../hooks';
import Smooch from 'smooch';

// Redux
import { addRawParams } from 'actions/config';
import { selectIsMobile } from 'reducers/main';
import { getIntentsBySelectedTag } from 'actions/workflows-v2/workflowsV2';
import { useUpdateConversation } from 'components/App/hooks';

// Components
import OptionButton from '../OptionButton';
import UserMessage from 'components/UserMessage';
import AgathaMessage from '../AgathaMessage/AgathaMessage';

// Types
import type { ButtonComponent, ButtonOptions } from 'api/types';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectError,
  selectIsAuthorIconVisible,
  selectIsLiveChatMode,
  setDismissableNotification,
} from 'reducers/workflows';
import {
  selectChatId,
  selectIntegrationConfig,
  selectIsInAssistantMode,
  selectLiveChatIntegration,
} from 'reducers/live-chat';
import { applyZopimFunction } from 'embed/zopim';
import { addLiveChatEventMessage, sendUserReply } from 'actions/live-chat';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import { selectWidgetInitMethod } from 'reducers/config';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { sendChoiceMessage } from 'components/salesforce-messaging-one-chat/messagingService';
import { v4 as uuidv4 } from 'uuid';
import { getErrorMessage } from 'utils/utils';
import { selectFeatureFlags } from 'reducers/workflows/index';
import { getSmoochErrorMessage } from 'embed/smooch';
import { getSmoochLocalStorage } from 'components/App/hooks/useHandleSuncoInitialization';

interface SingleSelectProps {
  isLastStep: boolean;
  /** Sets the selected option as context variables on the step */
  setStepContextVariables: Function;
  /** Sets the step as submitted -> when the user clicks on an option */
  setStepSent: (query?: string) => void;
  stepIndex: number;
  /** Object containing data to display */
  widgetComponent: ButtonComponent;
}

const SingleSelect: React.FC<React.PropsWithChildren<SingleSelectProps>> = ({
  isLastStep,
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const updateConversation = useUpdateConversation();
  const isMobile = useAppSelector(selectIsMobile);
  const error = useAppSelector(selectError);
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const isInAssistantMode = useAppSelector(selectIsInAssistantMode);
  const chatId = useAppSelector(selectChatId);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const conversationId = useAppSelector(selectConversationId);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const isCenterAlignButtonOptionsEnabled = featureFlags.includes(
    'center_align_button_options',
  );
  const contextVariables = widgetComponent.contextVariables || {};
  const componentFields = widgetComponent.component_fields;
  const outputVariable = componentFields.output_variable;
  const isSent = !!widgetComponent.isSent;
  const isReroutedByNegativeFeedback =
    !!widgetComponent.isReroutedByNegativeFeedback;
  const shouldConfirmTag = !!componentFields.should_confirm_tag;

  const selectedOption = componentFields?.options.find(option => {
    return option.value === contextVariables[outputVariable];
  });

  const onSubmit = async (selectedValue: string, displayName?: string) => {
    dispatch(setStepContextVariables({ [outputVariable]: selectedValue }));

    // one chats
    if (isLiveChatMode) {
      switch (liveChatIntegration) {
        case 'zendesk':
          applyZopimFunction(widgetInitMethod, 'sendChatMsg', selectedValue);
          break;
        case 'kustomer':
          applyKustomerCoreFunctionWithCallback('sendMessage', {
            body: displayName,
            conversationId: chatId,
            isInAssistantMode,
            payload: selectedValue,
          })
            .then(() => {
              dispatch(
                sendUserReply({
                  addWidgetComponent: false,
                  message: displayName || selectedValue,
                }),
              );
            })
            .catch((err: unknown) => {
              const errorMessage = 'Message failed to send';
              dispatch(addScreenReaderNotification(errorMessage));
              dispatch(addLiveChatEventMessage({ message: errorMessage }));
              sendErrorToSentry({
                additionalContext: { chatId, displayName, selectedValue },
                conversationId: conversationId,
                error: `Kustomer One Chat failed to send single select message -- ${err}`,
              });
            });
          break;
        case 'sunco':
          try {
            await Smooch.sendMessage(
              {
                // @ts-expect-error Smooch types are outdated, `payload` is actually supported
                payload: selectedValue,
                text: displayName || selectedValue,
                type: 'text',
              },
              chatId,
            );
            dispatch(setDismissableNotification(''));
          } catch (error) {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(setDismissableNotification(errorMessage));
            sendErrorToSentry({
              additionalContext: {
                ...getSmoochLocalStorage(integrationId),
              },
              conversationId: conversationId,
              error: `Sunco Live Chat failed to send single select message -- ${getSmoochErrorMessage(error)}`,
            });
          }
          break;
        case 'salesforce':
          sendChoiceMessage(
            chatId,
            selectedValue,
            uuidv4(),
            widgetComponent.component_fields.quotedMessageId || '',
          ).catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { chatId, displayName, selectedValue },
              conversationId,
              error: `Salesforce One Chat: ${errorMessage} -- ${getErrorMessage(err)}`,
            });
          });
          break;
      }
      setStepSent();
      return;
    }

    // normal workflow
    if (shouldConfirmTag) {
      dispatch(addRawParams({ 'data-ft-workflow-tag': selectedValue }));
      dispatch(getIntentsBySelectedTag());
    } else {
      updateConversation({ [outputVariable]: selectedValue });
    }

    setStepSent();
  };

  if (isReroutedByNegativeFeedback) {
    return (
      <AgathaMessage
        isAuthorIconVisible={isAuthorIconVisible}
        stepIndex={stepIndex}
      >
        {componentFields.prompt}
      </AgathaMessage>
    );
  }

  // `!isLastStep` check is needed to avoid rendering options for previous
  // option steps that weren't sent.
  // It can happen with Kustomer One Chat, for example (it can send an option
  // step at any point in a conversation).
  if (isSent || !isLastStep) {
    return (
      <>
        <AgathaMessage
          agentIcon={componentFields.agentIcon}
          agentName={componentFields.agentName}
          hideTimestamp={widgetComponent.hideTimestamp}
          isAuthorIconVisible={isAuthorIconVisible}
          isLiveChat={widgetComponent.isInAgentConvo}
          stepIndex={stepIndex}
          timestamp={widgetComponent.timestamp}
        >
          {componentFields.prompt}
        </AgathaMessage>
        {selectedOption?.display_name && (
          <UserMessage stepIndex={stepIndex}>
            {selectedOption.display_name}
          </UserMessage>
        )}
      </>
    );
  }

  return (
    <Container>
      <AgathaMessage
        agentIcon={componentFields.agentIcon}
        agentName={componentFields.agentName}
        hideTimestamp={widgetComponent.hideTimestamp}
        isAuthorIconVisible={isAuthorIconVisible}
        isLiveChat={widgetComponent.isInAgentConvo}
        stepIndex={stepIndex}
        timestamp={widgetComponent.timestamp}
      >
        {componentFields.prompt}
      </AgathaMessage>
      <ContentContainer
        isCenterAlignButtonOptionsEnabled={isCenterAlignButtonOptionsEnabled}
      >
        {componentFields?.options.map((option: ButtonOptions) => (
          <OptionButton
            isDisabled={Boolean(error)}
            isMobile={isMobile}
            key={option.value}
            onClick={() => onSubmit(option.value, option.display_name)}
            text={option.display_name}
            url={option.url}
            urlTarget={option.url_config?.target_attribute}
          />
        ))}
      </ContentContainer>
    </Container>
  );
};

export default SingleSelect;

const Container = styled('form')`
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled('div')<{
  isCenterAlignButtonOptionsEnabled?: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${props =>
    props.isCenterAlignButtonOptionsEnabled ? 'center' : 'flex-end'};
  justify-content: center;
  padding-bottom: 16px;
  padding-left: ${props =>
    props.isCenterAlignButtonOptionsEnabled ? '0' : '24px'}
  margin-top: 4px;
`;
